import { useEffect, useRef, useState, useCallback } from "react";
import { showBasicAlert } from "../../core/common/useAlert";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CallUser {
  id: string;
  name: string;
  avatar: string;
}

export type CallStatus =
  | "idle"
  | "ringing_out"
  | "ringing_in"
  | "connected"
  | "ended";

export interface CallState {
  status: CallStatus;
  channelId: string | null;
  caller: CallUser | null;
  callee: CallUser | null;
  callId: string | null;
}

const INITIAL_CALL_STATE: CallState = {
  status: "idle",
  channelId: null,
  caller: null,
  callee: null,
  callId: null,
};

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWebRTCCall({
  callingSocket,
  chatSocket,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  onCallMessage,
}: {
  callingSocket: any;
  chatSocket: any;
  currentUserId: string;
  currentUserName: string;
  currentUserAvatar: string;
  onCallMessage: (
    channelId: string,
    status: "missed" | "answered" | "rejected",
    duration?: number,
    callerId?: string,
  ) => void;
}) {
  const [callState, setCallState] = useState<CallState>(INITIAL_CALL_STATE);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [needsManualPlay, setNeedsManualPlay] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidate[]>([]);
  const pendingOfferRef = useRef<{
    offer: RTCSessionDescriptionInit;
    channelId: string;
    callerId: string;
    callId: string;
  } | null>(null);
  const callStateRef = useRef<CallState>(INITIAL_CALL_STATE);
  const callDurationRef = useRef<number>(0);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);
  useEffect(() => {
    callDurationRef.current = callDuration;
  }, [callDuration]);

  // ── Ringtone ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const audio = new Audio("/assets/sounds/ringtone.mp3");
    audio.loop = true;
    audio.addEventListener("error", () =>
      console.warn("Ringtone file not found"),
    );
    ringtoneRef.current = audio;
    return () => {
      audio.pause();
      ringtoneRef.current = null;
    };
  }, []);

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const startRingtone = () => {
    if (ringtoneRef.current) ringtoneRef.current.play().catch(() => {});
  };

  const startDurationTimer = () => {
    setCallDuration(0);
    durationTimerRef.current = setInterval(
      () => setCallDuration((prev) => prev + 1),
      1000,
    );
  };

  const stopDurationTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const playRemoteStream = (stream: MediaStream) => {
    const audio = new Audio();
    audio.srcObject = stream;
    audio.muted = false;
    audio.volume = 1.0;
    (audio as any).playsInline = true;

    audio
      .play()
      .then(() => {
        setNeedsManualPlay(false);
      })
      .catch(() => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.muted = false;
          remoteAudioRef.current.volume = 1.0;
          remoteAudioRef.current
            .play()
            .then(() => setNeedsManualPlay(false))
            .catch(() => setNeedsManualPlay(true));
        } else {
          setNeedsManualPlay(true);
        }
      });

    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = stream;
  };

  const closePeerConnection = () => {
    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
  };

  const stopLocalStream = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
  };

  const flushPendingCandidates = async (pc: RTCPeerConnection) => {
    const queued = pendingCandidatesRef.current.splice(0);
    for (const c of queued) {
      try {
        await pc.addIceCandidate(c);
      } catch (e) {
        console.error("ICE flush error:", e);
      }
    }
  };

  const teardown = useCallback(() => {
    stopRingtone();
    stopDurationTimer();
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
    closePeerConnection();
    stopLocalStream();
    pendingCandidatesRef.current = [];
    pendingOfferRef.current = null;
    setIsMuted(false);
    setCallDuration(0);
    setNeedsManualPlay(false);
    setCallState(INITIAL_CALL_STATE);
  }, []);

  const createPeerConnection = () => {
    closePeerConnection();
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.oniceconnectionstatechange = () =>
      (pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected"
        )
          hangup();
      });
    pc.ontrack = (event) => {
      if (event.track.kind !== "audio") return;
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      playRemoteStream(stream);
    };
    pcRef.current = pc;
    return pc;
  };

  const clearServerBusyState = useCallback(() => {
    if (!callingSocket) return;
    callingSocket.emit("call:force-clear", {}, (response: any) => {});
  }, [callingSocket]);

  // ── Outgoing call ──────────────────────────────────────────────────────────
  const startCall = useCallback(
    async (
      targetUser: { id: string; name: string; avatar: string },
      channelId: string,
    ) => {
      if (!callingSocket) {
        console.error("No calling socket");
        return;
      }

      // ✅ Clear any stuck busy state before attempting a new call
      clearServerBusyState();

      pendingCandidatesRef.current = [];

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch {
        showBasicAlert(
          "Could not access microphone. Please check permissions.",
        );
        return;
      }

      localStreamRef.current = stream;
      const pc = createPeerConnection();

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          callingSocket.emit("call:ice-candidate", {
            candidate: event.candidate,
            channelId,
            targetUserId: Number(targetUser.id),
          });
        }
      };

      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);

      setCallState({
        status: "ringing_out",
        channelId,
        caller: {
          id: currentUserId,
          name: currentUserName,
          avatar: currentUserAvatar,
        },
        callee: targetUser,
        callId: null,
      });

      callingSocket.emit(
        "call:initiate",
        {
          channelId,
          targetUserId: Number(targetUser.id),
          offer,
          callType: "audio",
          callerName: currentUserName,
          callerAvatar: currentUserAvatar,
        },
        (response: any) => {
          if (response?.success) {
            // ✅ Store callId from server — needed for call:end to work correctly
            if (response.callId) {
              setCallState((prev) => ({ ...prev, callId: response.callId }));
            }
          } else if (response && !response.success) {
            const msg =
              response.error === "USER_BUSY"
                ? `${targetUser.name} is already in a call`
                : response.error === "USER_OFFLINE"
                  ? `${targetUser.name} is offline`
                  : response.message || "Call failed";
            showBasicAlert(msg);
            teardown();
          }
        },
      );

      callTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current.status === "ringing_out") hangup();
      }, 30000);
    },
    [
      callingSocket,
      currentUserId,
      currentUserName,
      currentUserAvatar,
      teardown,
      clearServerBusyState,
    ],
  );

  // ── Accept incoming call ───────────────────────────────────────────────────
  const acceptCall = useCallback(async () => {
    const pending = pendingOfferRef.current;
    if (!pending || !callingSocket) return;

    stopRingtone();
    pendingCandidatesRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      showBasicAlert("Could not access microphone.");
      rejectCall();
      return;
    }

    localStreamRef.current = stream;
    const pc = createPeerConnection();

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        callingSocket.emit("call:ice-candidate", {
          candidate: event.candidate,
          channelId: pending.channelId,
          targetUserId: Number(pending.callerId),
        });
      }
    };

    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    await pc.setRemoteDescription(new RTCSessionDescription(pending.offer));

    await flushPendingCandidates(pc);

    const answer = await pc.createAnswer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await pc.setLocalDescription(answer);

    // ✅ callId is sent so backend can find the call in activeCalls
    callingSocket.emit("call:accept", {
      callId: pending.callId,
      channelId: pending.channelId,
      targetUserId: Number(pending.callerId),
      answer,
    });

    setCallState((prev) => ({
      ...prev,
      status: "connected",
      callId: pending.callId,
    }));
    pendingOfferRef.current = null;
    startDurationTimer();
  }, [callingSocket, teardown]);

  // ── Reject incoming call ───────────────────────────────────────────────────
  const rejectCall = useCallback(() => {
    stopRingtone();
    const state = callStateRef.current;
    const pending = pendingOfferRef.current;

    if (callingSocket) {
      const callId = pending?.callId || state.callId;
      const channelId = state.channelId || pending?.channelId;
      const callerId = String(state.caller?.id || pending?.callerId || "");

      if (channelId) {
        // ✅ Always send callId so backend can clean up activeCalls + userCallMap
        callingSocket.emit("call:reject", {
          callId,
          channelId,
          targetUserId: callerId ? Number(callerId) : null,
          reason: "rejected",
        });
        onCallMessage(channelId, "rejected", undefined, callerId);
      }
    }

    pendingOfferRef.current = null;
    teardown();
  }, [callingSocket, onCallMessage, teardown]);

  // ── Hang up ────────────────────────────────────────────────────────────────
  const hangup = useCallback(() => {
    const state = callStateRef.current;
    const duration = callDurationRef.current;

    if (state.status === "connected" && state.channelId) {
      onCallMessage(state.channelId, "answered", duration, state.caller?.id);
    } else if (state.status === "ringing_out" && state.channelId) {
      // Caller hung up before the callee answered
      onCallMessage(state.channelId, "missed", undefined, state.caller?.id);
    }

    if (callingSocket && state.channelId) {
      const targetId =
        state.caller?.id === currentUserId
          ? state.callee?.id
          : state.caller?.id;

      // ✅ FIX: Send callId so backend's call:end handler can find activeCalls entry
      // Without callId, activeCalls.get(undefined) returns nothing → userCallMap never cleaned
      callingSocket.emit("call:end", {
        callId: state.callId,
        channelId: state.channelId,
        targetUserId: targetId ? Number(targetId) : null,
        duration,
      });
    }

    teardown();
  }, [callingSocket, currentUserId, onCallMessage, teardown]);

  // ── Toggle mute ────────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach((t) => {
      t.enabled = isMuted;
    });
    setIsMuted((m) => !m);
  }, [isMuted]);

  // ── Manual play ────────────────────────────────────────────────────────────
  const manualPlay = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current
        .play()
        .then(() => setNeedsManualPlay(false))
        .catch(console.error);
    }
  }, []);

  // ── Socket event handlers ──────────────────────────────────────────────────
  const onIncomingCall = useCallback(
    (data: any) => {
      const { callerId, callerName, callerAvatar, offer, channelId, callId } =
        data;

      if (String(callerId) === String(currentUserId)) return;

      pendingOfferRef.current = {
        offer,
        channelId,
        callerId: String(callerId),
        callId,
      };

      setCallState({
        status: "ringing_in",
        channelId,
        caller: {
          id: String(callerId),
          name: callerName,
          avatar: callerAvatar,
        },
        callee: {
          id: currentUserId,
          name: currentUserName,
          avatar: currentUserAvatar,
        },
        callId,
      });

      startRingtone();
    },
    [currentUserId, currentUserName, currentUserAvatar],
  );

  const onCallAccepted = useCallback(
    async (data: any) => {
      const { answer, channelId, callId } = data;

      const state = callStateRef.current;
      if (!pcRef.current || state.channelId !== channelId) return;

      try {
        await pcRef.current.setRemoteDescription(
          new RTCSessionDescription(answer),
        );
        await flushPendingCandidates(pcRef.current);

        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }

        // ✅ Store callId when call is accepted (callee side sends it back via call:accepted)
        setCallState((prev) => ({
          ...prev,
          status: "connected",
          callId: callId || prev.callId, // keep whichever we have
        }));
        startDurationTimer();
      } catch (e) {
        console.error("Error setting answer:", e);
        hangup();
      }
    },
    [hangup],
  );

  const onCallRejected = useCallback(
    (data: any) => {
      const state = callStateRef.current;
      if (
        state.status === "ringing_out" &&
        state.channelId === data.channelId
      ) {
        // Remote side rejected — they already emitted the call record via rejectCall().
        // We just tear down locally; the new-message socket event will update the chat.
        teardown();
      }
    },
    [teardown],
  );

  const onCallEnded = useCallback(
    (data: any) => {
      const state = callStateRef.current;
      if (state.channelId !== data.channelId) return;

      stopRingtone();
      // Remote side hung up — they already emitted the call record via hangup().
      // We just tear down locally; the new-message socket event will update the chat.
      teardown();
    },
    [teardown],
  );

  const onIceCandidate = useCallback(async (data: any) => {
    const { candidate } = data;

    if (!pcRef.current) {
      pendingCandidatesRef.current.push(new RTCIceCandidate(candidate));
      return;
    }

    const pc = pcRef.current;
    if (!pc.remoteDescription) {
      pendingCandidatesRef.current.push(new RTCIceCandidate(candidate));
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (e) {
      console.error("ICE candidate error:", e);
    }
  }, []);

  const onNoAnswer = useCallback(
    (data: any) => {
      const state = callStateRef.current;
      if (
        state.channelId === data.channelId &&
        state.status === "ringing_out"
      ) {
        // Timeout fired — the caller's hangup() will emit the missed-call record.
        // Just tear down here.
        teardown();
      }
    },
    [teardown],
  );

  const onMissedCall = useCallback(
    (data: any) => {
      const state = callStateRef.current;
      if (state.channelId === data.channelId) {
        stopRingtone();
        // Server-side timeout for callee — the caller's side will emit the missed-call record.
        // Just tear down here.
        teardown();
      }
    },
    [teardown],
  );

  // ── Register socket listeners ONCE ────────────────────────────────────────
  useEffect(() => {
    if (!callingSocket) return;

    callingSocket.on("call:incoming", onIncomingCall);
    callingSocket.on("call:accepted", onCallAccepted);
    callingSocket.on("call:rejected", onCallRejected);
    callingSocket.on("call:ended", onCallEnded);
    callingSocket.on("call:ice-candidate", onIceCandidate);
    callingSocket.on("call:no-answer", onNoAnswer);
    callingSocket.on("call:missed", onMissedCall);

    // Force-cleanup on tab close/refresh mid-call
    const handleBeforeUnload = () => {
      const state = callStateRef.current;
      if (state.channelId && callingSocket.connected) {
        const targetId =
          state.caller?.id === currentUserId
            ? state.callee?.id
            : state.caller?.id;
        callingSocket.emit("call:end", {
          callId: state.callId,
          channelId: state.channelId,
          targetUserId: targetId ? Number(targetId) : null,
          duration: callDurationRef.current,
        });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      callingSocket.off("call:incoming", onIncomingCall);
      callingSocket.off("call:accepted", onCallAccepted);
      callingSocket.off("call:rejected", onCallRejected);
      callingSocket.off("call:ended", onCallEnded);
      callingSocket.off("call:ice-candidate", onIceCandidate);
      callingSocket.off("call:no-answer", onNoAnswer);
      callingSocket.off("call:missed", onMissedCall);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingSocket]);

  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  return {
    callState,
    isMuted,
    callDuration,
    needsManualPlay,
    remoteAudioRef,
    startCall,
    acceptCall,
    rejectCall,
    hangup,
    toggleMute,
    manualPlay,
    formatDuration,
  };
}
